import { Tooltip, IconButton, Card, CardContent, CardHeader, Drawer, useMediaQuery, Box, 
  Typography, Grid, FormControl, InputLabel, MenuItem, Select, Button, FormHelperText, FormControlLabel, Checkbox } from "@mui/material";
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { ReduxState, useRecordContext } from "ra-core";
import { RecPatch } from "@/dataProvider/tools";
import { CopyFileTemplateMappingInput, FileEncryptionTemplateDto } from "@/gen/api/encrypt";
import CreateOrEdit from "@/controller/CreateOrEdit";
import WithSave from "@/controller/WithSave";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { useResourceRegister } from "@/utils/resourceRegister";
import { useDispatch, useSelector } from "react-redux";
import { crudGetOneEx } from "@/actions/crudFetchEx";


const resource = 'copy-file-mappings'

const CopyFileTemplateMapping: FC = () => {
  useResourceRegister({ name: resource })

  const [states, setStates] = useState({
    open: false,
    sourceTemplateId: '' as number|'',
    includeObfs: false,
    errors: null as string|null,
    isSubmitting: false,
  })
  const record = useRecordContext<FileEncryptionTemplateDto&RecPatch>()
  const dispatch = useDispatch()
  const recordIdRef = useRef(record?.id)
  recordIdRef.current = record?.id
  const refetch = useCallback(() => {
    recordIdRef.current && dispatch(crudGetOneEx('file-encrypt-template', recordIdRef.current, ''))
  }, [])

  const handleClose = (): void => {
    setStates(v => ({ open: false, sourceTemplateId: '', includeObfs: false, errors: null, isSubmitting: false }));
  };

  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const tmplData = useSelector<ReduxState, FileEncryptionTemplateDto[]|undefined>(state => {
    const resStore = state.admin.resources['file-encrypt-template']
    return (resStore &&
      (resStore.list.ids ?? []).map(id => resStore.data[id])) as any
  })
  const tmplsForSelect = useMemo(() => {
    if (!states.open || !record) { return [] }
    else {
      const targetTemplate = record
      return (tmplData ?? []).filter(x => x.kind === targetTemplate.kind && x.templateId !== targetTemplate.templateId)
        .sort((a, b) => (a.name || '').toUpperCase() < (b.name || '').toUpperCase() ? -1 : 1)
    }
  }, [states.open, record, tmplData])

  const handlers = useDefaultSavedHandlers("Copy template mappings")
  handlers.onSuccess.merge(useCallback(() => {
    setStates(v => ({ open: false, sourceTemplateId: '', includeObfs: false, errors: null, isSubmitting: false }));
    refetch && refetch()
  }, [refetch]))
  handlers.onFailure.merge(useCallback((err:any) => {
    const errMsg = retrieveAxiosErr(err)
    setStates(v => ({...v, isSubmitting: false, errors: errMsg }))
  }, []))
  
  if (!record) { return null }
  const targetTemplate = record

  return (
    <>
      <Tooltip title="Copy Mappings">
        <IconButton size="small" onClick={() => setStates(v => ({...v, open: true}))}>
          <FormatPaintIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Drawer
        anchor="right"
        onClose={handleClose}
        open={states.open}
        PaperProps={{ sx: { p: 2, width: width, } }}
      >
        <Card sx={{ overflowY: 'auto', mb: 4, }} >
          <CardHeader title="Copy Mappings" />
          <CardContent>
            <Box sx={{ '& .MuiTypography-body2': { mb: 2 } }}>
              <Typography variant="body2">{targetTemplate.name}</Typography>
              <Typography variant="body2" color="InactiveCaptionText">Copy from:</Typography>
            </Box>
            <Grid container spacing={4} >
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="SourceTemplate-label">Source Template*</InputLabel>
                  <Select fullWidth
                    label="Source Template*" 
                    labelId="SourceTemplate-label" 
                    id="SourceTemplate" 
                    name="SourceTemplate" 
                    value={states.sourceTemplateId}
                    onChange={(e) => {
                      const sourceTemplateId = parseInt(e.target.value as string)
                      if (!isNaN(sourceTemplateId)) {
                        setStates(v => ({...v, sourceTemplateId, }))
                      }
                    }}
                  >
                    {tmplsForSelect.map(t => (
                      <MenuItem key={t.templateId} value={t.templateId}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel label="Include Obfuscation Rule Mapping"
                  control={
                    <Checkbox 
                      checked={states.includeObfs}
                      onChange={(e) => setStates(v => ({...v, includeObfs: e.target.checked })) }
                    />}
                />
              </Grid>
            </Grid>
            {states.errors && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {states.errors}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
            <Button
              color="primary"
              disabled={states.isSubmitting}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <CreateOrEdit resource={resource} id={targetTemplate.id} record={targetTemplate} {...handlers} basePath="">
              <WithSave 
                render={({ save, saving }) => (
                  <Button
                    color="primary"
                    disabled={!states.sourceTemplateId || saving}
                    type="submit"
                    variant="contained"
                    onClick={() => {
                      setStates(v => ({...v, isSubmitting: true }))
                      const payload : CopyFileTemplateMappingInput = {
                        templateId: targetTemplate.templateId,
                        copyFromTemplateId: +states.sourceTemplateId,
                        includeObfsRule: states.includeObfs,
                      }
                      save(payload)
                    }}
                  >
                    Copy
                  </Button>
                )}
              />
            </CreateOrEdit>
          </Box>
        </Card>
      </Drawer>
    </>
  )
}

export default CopyFileTemplateMapping;
